import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, InfoIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { DocumentFormTemplate } from './formTemplates';

type Props = {
  template: DocumentFormTemplate;
  documentTypeName?: string;
};

export const DocumentFormTemplateBanner: React.FC<Props> = ({ template, documentTypeName }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);

  const title = documentTypeName ?? template.label;
  const hasBody = Boolean(template.description || template.hints.length || template.entryRoute);

  const tone =
    template.id === 'payment'
      ? colors.info
      : template.id === 'receipt'
        ? colors.success
        : template.id === 'invoice'
          ? colors.info
          : colors.surfaceHover;

  const iconTone =
    template.id === 'payment'
      ? colors.primaryFg
      : template.id === 'receipt'
        ? isDark
          ? 'text-emerald-300'
          : 'text-emerald-600'
        : template.id === 'invoice'
          ? isDark
            ? 'text-sky-300'
            : 'text-sky-600'
          : colors.textMuted;

  return (
    <div className={cn('rounded-lg border', colors.border, tone)}>
      <button
        type="button"
        onClick={() => hasBody && setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-1.5 text-left',
          hasBody && colors.interactiveHover,
          !hasBody && 'cursor-default',
        )}
        aria-expanded={open}
      >
        <InfoIcon className={cn('h-3.5 w-3.5 shrink-0', iconTone)} />
        <span className={cn('min-w-0 flex-1 truncate text-xs', colors.text)}>
          <span className="font-medium">{title}</span>
          <span className={colors.textMuted}> — {template.label} layout</span>
        </span>
        {hasBody && (
          <ChevronDownIcon
            className={cn('h-3.5 w-3.5 shrink-0 transition-transform', colors.textMuted, open && 'rotate-180')}
          />
        )}
      </button>

      {open && hasBody && (
        <div className={cn('space-y-1 border-t px-3 pb-2.5 pt-0', colors.border)}>
          {template.description && (
            <p className={cn('pt-2 text-xs leading-relaxed', colors.textMuted)}>{template.description}</p>
          )}
          {template.hints.length > 0 && (
            <ul className={cn('list-disc space-y-0.5 pl-4 text-xs leading-relaxed', colors.textMuted)}>
              {template.hints.map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
          )}
          {template.entryRoute && (
            <p className={cn('pt-0.5 text-xs', colors.textMuted)}>
              <Link to={template.entryRoute} className={cn('font-medium hover:underline', colors.primaryFg)}>
                Open dedicated {template.label.toLowerCase()} form
              </Link>
            </p>
          )}
        </div>
      )}
    </div>
  );
};
